import { Component } from '@theme/component';

/**
 * Se den på dig: pasformsvideoer fra produktets metafelt.
 *
 * Klippene ligger som <video preload="none"> med kilderne i data-src, så der
 * ikke hentes video, før man kan se den. Posteren er et almindeligt lazy
 * <img> bagved, og videoen fades ind, når den faktisk spiller.
 *
 * Teksterne på pause-knappen kommer fra block settings som data-attributter,
 * så de kan oversættes i Translate & Adapt.
 */

/** Hvor tidligt et klip hentes, før det er i syne. */
const ROOT_MARGIN = '200px';

/** Hvor stor en del af klippet der skal være synlig, før det spiller. */
const THRESHOLD = 0.25;

/**
 * @typedef {Object} Refs
 * @property {HTMLElement} [list]
 * @property {HTMLVideoElement[]} [videos]
 * @property {HTMLButtonElement} [toggle]
 *
 * @extends {Component<Refs>}
 */
class LocktownSeeOnYouComponent extends Component {
  /** Om klippene må spille. Følger brugerens valg og prefers-reduced-motion. */
  #playing = true;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {Set<HTMLVideoElement>} */
  #visible = new Set();

  connectedCallback() {
    super.connectedCallback();

    this.#playing = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#updateToggle();

    this.#observer = new IntersectionObserver(this.#onIntersect, {
      rootMargin: ROOT_MARGIN,
      threshold: THRESHOLD,
    });

    for (const video of this.#videos()) {
      video.addEventListener('playing', this.#onPlaying);
      this.#observer.observe(video);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    for (const video of this.#videos()) {
      video.removeEventListener('playing', this.#onPlaying);
    }

    this.#observer?.disconnect();
    this.#observer = null;
    this.#visible.clear();
  }

  /** @returns {HTMLVideoElement[]} */
  #videos() {
    const { videos } = this.refs;
    return Array.isArray(videos) ? videos : [];
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #onIntersect = (entries) => {
    for (const entry of entries) {
      const video = entry.target;
      if (!(video instanceof HTMLVideoElement)) continue;

      if (entry.isIntersecting) {
        this.#visible.add(video);
        if (this.#playing) this.#start(video);
      } else {
        this.#visible.delete(video);
        video.pause();
      }
    }
  };

  /**
   * Henter kilderne første gang og starter afspilningen.
   * @param {HTMLVideoElement} video
   */
  #start(video) {
    if (!video.dataset.loaded) {
      video.dataset.loaded = 'true';

      for (const source of video.querySelectorAll('source[data-src]')) {
        source.setAttribute('src', source.dataset.src ?? '');
      }

      // muted skal sættes som property. Attributten alene er ikke nok til at
      // Safari accepterer autoplay på et element, der får sin kilde fra JS.
      video.muted = true;
      video.load();
    }

    // Et afvist play() er browseren, der siger nej på autoplay-reglerne.
    // Der er intet at rette op på, og en ufanget rejection er bare støj.
    video.play().catch(() => {});
  }

  /**
   * @param {Event} event
   */
  #onPlaying = (event) => {
    const item = /** @type {Element} */ (event.target).closest('.locktown-see-on-you__item');
    if (item instanceof HTMLElement) item.dataset.playing = 'true';
  };

  /** Kaldes af pause-knappen via on:click. */
  togglePlayback() {
    this.#playing = !this.#playing;

    for (const video of this.#videos()) {
      if (this.#playing) {
        if (this.#visible.has(video)) this.#start(video);
      } else {
        video.pause();
      }
    }

    this.#updateToggle();
  }

  #updateToggle() {
    const { toggle } = this.refs;
    if (!(toggle instanceof HTMLButtonElement)) return;

    const { labelPause = '', labelPlay = '' } = this.dataset;
    toggle.textContent = this.#playing ? labelPause : labelPlay;
    toggle.setAttribute('aria-pressed', String(!this.#playing));
  }
}

if (!customElements.get('locktown-see-on-you-component')) {
  customElements.define('locktown-see-on-you-component', LocktownSeeOnYouComponent);
}
